import { fmtCL } from "@/lib/fecha";
// Impresión directa en la impresora de 80 mm del sistema operativo (Windows).
// Se imprime en un iframe oculto con su propio @page de 80 mm: así el papel sale sólo con el
// vale (no con la pantalla del POS) y, con Chrome/Edge en modo impresión directa
// (--kiosk-printing), sale sin ningún diálogo y a la impresora predeterminada.
import { tbkCuenta, tbkFechaContable } from "./btPrinter";
import QRCode from "qrcode";
import JsBarcode from "jsbarcode";
import html2canvas from "html2canvas";
import { clp } from "./api";
import { maskRut, formatRut } from "./rut";
import { saleReceiptLines } from "./saleLines";
import { APP_VERSION } from "./appVersion";

const esc = (s) =>
  String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const rowHtml = (a, b, cls = "") => `<div class="row ${cls}"><span>${a}</span><span>${b}</span></div>`;

// Mensaje de cierre del vale: configurable por terminal (varias líneas). Vacío = mensaje por defecto.
export const graciasLines = (msg) => {
  const lines = String(msg ?? "").split("\n").map((l) => l.trim()).filter(Boolean);
  return lines.length ? lines : ["¡Gracias por tu compra!"];
};
const graciasHtml = (msg) =>
  `<div class="sep"></div>` + graciasLines(msg).map((l) => `<div class="c b">${esc(l)}</div>`).join("");

// Tipografía compacta: el vale rinde más papel aunque la venta tenga muchos productos.
const BASE_CSS = `
  @page { size: 80mm auto; margin: 0; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  html, body { margin: 0; padding: 0; background: #fff; color: #000; }
  body {
    width: 80mm; padding: 2mm 3.5mm 6mm;
    font-family: "Consolas", "Courier New", monospace;
    font-size: 12.5px; line-height: 1.25; font-weight: 600; letter-spacing: 0;
  }
  .c { text-align: center; }
  .b { font-weight: 800; }
  .xl { font-size: 15px; font-weight: 800; }
  .xxl { font-size: 18px; font-weight: 800; }
  .sm { font-size: 11px; font-weight: 600; }
  .sep { border-top: 1px dashed #000; margin: 8px 0 4px; }
  .row { display: flex; justify-content: space-between; gap: 5px; align-items: baseline; }
  .row > span:first-child { flex: 1; min-width: 0; word-break: break-word; }
  .row > span:last-child { white-space: nowrap; }
  .row3 { display: flex; justify-content: space-between; gap: 4px; }
  .promo { font-weight: 800; }
  .logo { display: block; margin: 0 auto 3px; max-width: 42mm; max-height: 18mm;
          object-fit: contain; filter: grayscale(1) contrast(1.4); }
  .qr { display: block; margin: 1mm auto 0; width: 19mm; height: 19mm; }
  /* El QR va separado del pie: en 80 mm la última línea del mensaje se montaba encima. */
  .qr-wrap { margin-top: 6mm; text-align: center; page-break-inside: avoid; }
  .bc { display: block; margin: 2px auto 0; width: 68mm; }
  .ted { display: block; margin: 0 auto; width: 70mm; image-rendering: pixelated; }
  .ted-wrap { margin-top: 8mm; }
  .firma { margin-top: 4mm; border: 1.5px solid #000; padding: 2mm 3mm 3mm; font: inherit; }
  .firma .ln { display: flex; align-items: flex-end; gap: 2mm; margin-top: 8mm;
               font: inherit; font-size: 13px; font-weight: 800; }
  .firma .ln span:last-child { flex: 1; border-bottom: 1.5px solid #000; height: 1px; }
  .cedible { margin-top: 4mm; font-size: 22px; font-weight: 900; letter-spacing: 4px; text-align: center; }
  .sign { height: 16mm; }
  .wline { border-bottom: 1px solid #000; height: 9mm; margin: 0 0 1mm; }
`;

/** Imprime un fragmento HTML en la impresora del sistema. Resuelve {silent} según si el
 *  navegador mostró (o no) el diálogo de impresión. */
export function printHtml(bodyHtml, title = "Ticket", css = BASE_CSS) {
  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = `position:fixed;right:0;bottom:0;width:${css === BASE_CSS ? "80mm" : "216mm"};height:0;border:0;opacity:0;`;
    document.body.appendChild(iframe);

    const cleanup = (silent) => {
      setTimeout(() => { try { document.body.removeChild(iframe); } catch (_) {} }, 1200);
      resolve({ silent });
    };

    iframe.onload = async () => {
      // Logo, QR y código de barras deben estar cargados antes de imprimir
      try {
        const imgs = Array.from(iframe.contentWindow.document.images || []);
        await Promise.race([
          Promise.all(imgs.map((im) => im.complete ? null : new Promise((r) => {
            im.addEventListener("load", r, { once: true });
            im.addEventListener("error", r, { once: true });
          }))),
          new Promise((r) => setTimeout(r, 2500)),
        ]);
      } catch (_) { /* seguimos: mejor imprimir sin imagen que no imprimir */ }
      const t0 = Date.now();
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (_) {
        cleanup(false);
        return;
      }
      // Con el diálogo abierto, print() recién retorna cuando el usuario lo cierra.
      cleanup(Date.now() - t0 < 1200);
    };

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title>` +
      `<style>${css}</style></head><body>${bodyHtml}</body></html>`
    );
    doc.close();
  });
}

/** Guía de traslado interna (hoja carta) que viaja con la mercadería del traspaso.
 *  No reemplaza la Guía de Despacho electrónica del SII (DTE 52). */
const SHEET_CSS = `
  @page { size: letter; margin: 14mm 12mm; }
  * { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  body { margin: 0; font-family: "Segoe UI", Arial, sans-serif; color: #0f172a; font-size: 12px; }
  .head { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
          border-bottom: 2px solid #0f172a; padding-bottom: 8px; }
  .emp { font-size: 16px; font-weight: 800; }
  .muted { color: #64748b; font-size: 11px; }
  .folio { border: 2px solid #b91c1c; color: #b91c1c; border-radius: 6px; padding: 6px 12px;
           text-align: center; min-width: 46mm; }
  .folio .t { font-size: 10px; font-weight: 800; letter-spacing: .08em; }
  .folio .n { font-size: 20px; font-weight: 800; }
  .grid2 { display: flex; gap: 10px; margin: 12px 0; }
  .box { flex: 1; border: 1px solid #cbd5e1; border-radius: 6px; padding: 8px 10px; }
  .box .lbl { font-size: 9px; font-weight: 800; letter-spacing: .1em; color: #64748b; }
  .box .val { font-size: 13px; font-weight: 700; }
  table { width: 100%; border-collapse: collapse; margin-top: 6px; }
  th { background: #f1f5f9; font-size: 9px; letter-spacing: .08em; text-transform: uppercase;
       text-align: left; padding: 6px; border-bottom: 1px solid #cbd5e1; }
  td { padding: 6px; border-bottom: 1px solid #e2e8f0; font-size: 11.5px; }
  td.n, th.n { text-align: right; }
  .tot { font-weight: 800; font-size: 13px; }
  .firmas { display: flex; gap: 24px; margin-top: 28px; }
  .firma { flex: 1; text-align: center; font-size: 10px; color: #64748b; }
  .firma .line { border-top: 1px solid #0f172a; margin-bottom: 4px; height: 18mm; }
  .nota { margin-top: 14px; font-size: 10px; color: #64748b; border-top: 1px dashed #cbd5e1; padding-top: 8px; }
`;

export function transferGuideHtml(t, ctx = {}) {
  const lineas = t.lineas || [];
  const unidades = lineas.reduce((a, l) => a + Number(l.cantidad_enviada || 0), 0);
  const filas = lineas.map((l, i) => `<tr>
      <td class="n">${i + 1}</td>
      <td>${esc(l.sku || "")}</td>
      <td>${esc(l.product_name || l.product_id)}</td>
      <td class="n">${Number(l.cantidad_enviada || 0)}</td>
      <td class="n"></td>
    </tr>`).join("");
  const fecha = fmtCL(t.dispatched_at || t.created_at);
  return `
  <div class="head">
    <div>
      ${ctx.logoUrl ? `<img src="${esc(ctx.logoUrl)}" style="max-height:16mm;margin-bottom:4px" />` : ""}
      <div class="emp">${esc(ctx.companyName || "")}</div>
      <div class="muted">${esc(ctx.rut || "")}${ctx.giro ? " · " + esc(ctx.giro) : ""}</div>
      <div class="muted">${esc(ctx.direccion || "")}</div>
    </div>
    <div class="folio">
      <div class="t">GUÍA DE TRASLADO INTERNO</div>
      <div class="n">N° ${esc(t.guia_numero || "—")}</div>
      <div class="t">${esc(fecha)}</div>
    </div>
  </div>

  <div class="grid2">
    <div class="box">
      <div class="lbl">ORIGEN · SUCURSAL QUE ENVÍA</div>
      <div class="val">${esc(t.store_origen_nombre || "")}</div>
      <div class="muted">${esc(t.despachado_por || "")}</div>
    </div>
    <div class="box">
      <div class="lbl">DESTINO · SUCURSAL QUE RECIBE</div>
      <div class="val">${esc(t.store_destino_nombre || "")}</div>
      <div class="muted">Traspaso ${esc(t.transfer_id || "")}</div>
    </div>
  </div>

  <table>
    <thead><tr>
      <th class="n">#</th><th>SKU</th><th>Producto</th>
      <th class="n">Enviado</th><th class="n">Recibido</th>
    </tr></thead>
    <tbody>${filas}</tbody>
    <tfoot><tr>
      <td colspan="3" class="tot">TOTAL UNIDADES</td>
      <td class="n tot">${unidades}</td><td class="n"></td>
    </tr></tfoot>
  </table>

  ${t.nota || t.nota_despacho ? `<div class="nota">Nota: ${esc(t.nota_despacho || t.nota)}</div>` : ""}

  <div class="firmas">
    <div class="firma"><div class="line"></div>Despacha (origen)</div>
    <div class="firma"><div class="line"></div>Transporta</div>
    <div class="firma"><div class="line"></div>Recibe (destino)</div>
  </div>

  <div class="nota">
    Documento de control interno para el traslado de mercadería entre sucursales de la misma empresa.
    No constituye Guía de Despacho electrónica del SII (DTE 52).
  </div>`;
}

export function printTransferGuide(t, ctx = {}) {
  return printHtml(transferGuideHtml(t, ctx), `Guía de traslado ${t.guia_numero || ""}`, SHEET_CSS);
}

/** QR como data URL (apunta a la boleta digital en la web). */
export async function qrDataUrl(text) {
  try {
    return await QRCode.toDataURL(String(text), { margin: 0, width: 320, errorCorrectionLevel: "M" });
  } catch { return null; }
}

/** Renderiza un fragmento de ticket (mismo HTML/CSS del vale de 80 mm) a PNG base64.
 *  Se usa para la ruta "raster" del agente local cuando el vale lleva logo. */
export async function renderTicketPng(bodyHtml, title = "Ticket") {
  return new Promise((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.cssText = "position:fixed;left:-10000px;top:0;width:80mm;height:auto;border:0;background:#fff;";
    document.body.appendChild(iframe);
    const done = (png) => {
      setTimeout(() => { try { document.body.removeChild(iframe); } catch (_) {} }, 300);
      resolve(png);
    };
    iframe.onload = async () => {
      try {
        const doc = iframe.contentWindow.document;
        const imgs = Array.from(doc.images || []);
        await Promise.race([
          Promise.all(imgs.map((im) => im.complete ? null : new Promise((r) => {
            im.addEventListener("load", r, { once: true });
            im.addEventListener("error", r, { once: true });
          }))),
          new Promise((r) => setTimeout(r, 1500)),
        ]);
        const body = doc.body;
        const canvas = await html2canvas(body, {
          backgroundColor: "#fff", scale: 2.4,
          width: body.scrollWidth, height: body.scrollHeight, windowWidth: body.scrollWidth,
        });
        done(canvas.toDataURL("image/png").split(",")[1] || null);
      } catch (_) { done(null); }
    };
    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(
      `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)}</title>` +
      `<style>${BASE_CSS}</style></head><body>${bodyHtml}</body></html>`
    );
    doc.close();
  });
}

/** Código de barras (Code 39 / EAN-13) como data URL para el vale de pre-venta. */
export function barcodeDataUrl(value, format = "CODE39") {
  try {
    const canvas = document.createElement("canvas");
    JsBarcode(canvas, String(value), {
      format: format === "EAN13" ? "EAN13" : "CODE39",
      width: 2, height: 70, displayValue: true, fontSize: 16, margin: 4, background: "#ffffff",
    });
    return canvas.toDataURL("image/png");
  } catch { return null; }
}

/** URL pública de la representación digital de la boleta/vale. */
export function receiptUrl(saleId) {
  return `${window.location.origin}/boleta/${saleId}`;
}

/** Encabezado común (logo + empresa + sucursal) de todos los comprobantes de 80 mm. */
function headHtml(companyName, storeName, titulo, logoUrl) {
  const out = [];
  if (logoUrl) out.push(`<img class="logo" src="${esc(logoUrl)}" alt="">`);
  out.push(`<div class="c xl">${esc(companyName || "")}</div>`);
  if (storeName) out.push(`<div class="c sm">${esc(storeName)}</div>`);
  if (titulo) out.push(`<div class="c b">${esc(titulo)}</div>`);
  out.push(`<div class="c sm">${esc(new Date().toLocaleString("es-CL"))}</div>`);
  out.push(`<div class="sep"></div>`);
  return out.join("");
}

/** Vale de venta tal cual se ve en pantalla (promociones, cajero y QR de la boleta digital). */
/** Comprobante de venta Transbank (copia cliente) con los datos devueltos por el POS integrado. */
export function tbkVoucherHtml(tbk) {
  if (!tbk?.autorizacion) return "";
  const c = tbk.comercio_datos || {};
  const f = (v, sep) => { const x = String(v || ""); return x.length === 8 ? [x.slice(0, 2), x.slice(2, 4), x.slice(4)].join(sep) : x; };
  const h = (v) => { const x = String(v || ""); return x.length === 6 ? `${x.slice(0, 2)}:${x.slice(2, 4)}:${x.slice(4)}` : x; };
  const fc = tbkFechaContable(tbk.fecha_contable);
  const credito = String(tbk.tipo_tarjeta || "").toUpperCase().startsWith("CR");
  const out = [`<div class="sep"></div><div class="tbk">`];
  out.push(`<div class="c b">COMPROBANTE DE VENTA</div>`);
  out.push(`<div class="c xl">TARJETA DE ${credito ? "CREDITO" : "DEBITO"}</div>`);
  if (c.nombre_fantasia) out.push(`<div class="c b">${esc(String(c.nombre_fantasia).toUpperCase())}</div>`);
  if (c.razon_social) out.push(`<div class="c">${esc(String(c.razon_social).toUpperCase())}</div>`);
  if (c.direccion) out.push(`<div class="c sm">${esc(String(c.direccion).toUpperCase())}</div>`);
  if (c.rut) out.push(`<div class="c">${esc(c.rut)}</div>`);
  if (c.ciudad) out.push(`<div class="c sm">${esc(String(c.ciudad).toUpperCase())}</div>`);
  out.push(`<div class="c sm">${esc([tbk.comercio, tbk.terminal].filter(Boolean).join("-"))}</div>`);
  out.push(`<div class="sep"></div>`);
  out.push(`<div class="row3 sm b"><span>FECHA</span><span>HORA</span><span>TERMINAL</span></div>`);
  out.push(`<div class="row3 sm"><span>${esc(f(tbk.fecha, "/"))}</span><span>${esc(h(tbk.hora))}</span><span>${esc(tbk.terminal || "")}</span></div>`);
  if (fc) out.push(rowHtml("FECHA CONTABLE", esc(fc), "sm"));
  out.push(rowHtml("NUMERO DE TARJETA", `************${esc(tbk.ultimos4 || "")}`, "sm"));
  if (!credito && tbkCuenta(tbk.num_cuenta)) out.push(rowHtml("NUMERO DE CUENTA", esc(tbkCuenta(tbk.num_cuenta)), "sm"));
  out.push(`<div class="b">${esc(String(tbk.marca || "").toUpperCase())} ${credito ? "CREDITO" : "DEBITO"}</div>`);
  out.push(rowHtml("MONTO COMPRA", clp(tbk.monto)));
  if (credito && Number(tbk.cuotas) > 1) {
    out.push(rowHtml("N° CUOTAS", esc(tbk.cuotas)));
    if (tbk.monto_cuota) out.push(rowHtml("MONTO CUOTA", clp(tbk.monto_cuota)));
  }
  out.push(rowHtml(`<span class="xl">TOTAL:</span>`, `<span class="xl">${clp(tbk.monto)}</span>`));
  out.push(rowHtml("NUMERO DE OPERACION", esc(tbk.operacion || ""), "sm"));
  out.push(rowHtml("CODIGO DE AUTORIZACION", esc(tbk.autorizacion), "sm"));
  out.push(`<div class="sep"></div><div class="c sm">GRACIAS POR SU COMPRA</div><div class="c sm">COPIA CLIENTE</div>`);
  out.push(`<div class="c sm">ACEPTO PAGAR SEGUN CONTRATO CON EMISOR</div></div>`);
  return out.join("");
}

// Tarjeta Propia: cliente y cupo que le queda después de esta compra
function creditoHtml(c) {
  return `<div class="sep"></div><div class="b">${esc(c.medio || "Tarjeta Propia")}</div><div class="sm">${esc(c.nombre || "")}</div>`
    + (c.rut ? `<div class="sm">RUT ${esc(formatRut(c.rut))}</div>` : "")
    + rowHtml("<b>Cupo disponible</b>", `<b>${clp(c.disponible)}</b>`);
}

export function saleTicketHtml(sale, companyName, storeName, logoUrl, footerMessage, opts = {}) {
  if (!sale) return "";
  const out = [];

  if (logoUrl) out.push(`<img class="logo" src="${esc(logoUrl)}" alt="">`);
  out.push(`<div class="c xl">${esc(companyName || "")}</div>`);
  if (storeName) out.push(`<div class="c sm">${esc(storeName)}</div>`);
  if (sale.offline) out.push(`<div class="c sm b">COMPROBANTE OFFLINE</div>`);
  if (sale.payment_is_simulated) out.push(`<div class="c sm b">PAGO SIMULADO · PRUEBAS</div>`);
  const esTicket = sale.ticket_sin_dte || sale.emision?.doc_type === "TICKET";
  if (esTicket) out.push(`<div class="c xl">TICKET</div><div class="c sm">Documento interno · no válido como boleta</div>`);
  out.push(`<div class="c b">VENTA #${esc(sale.numero)}</div>`);
  out.push(`<div class="c sm">${esc(new Date(sale.created_at || Date.now()).toLocaleString("es-CL"))}</div>`);
  if (opts.caja) out.push(`<div class="c sm">Caja: ${esc(opts.caja)}</div>`);
  if (opts.cashier) out.push(`<div class="c sm">Cajero: ${esc(opts.cashier)}</div>`);
  // Tickets de pre-venta cobrados en esta boleta
  const presCodes = sale.presale_codes || opts.presaleCodes || [];
  if (presCodes.length) {
    out.push(`<div class="c sm b">Ticket Pre-venta: ${esc(presCodes.join("-"))}</div>`);
  }
  out.push(`<div class="sep"></div>`);

  const { items, subtotal_normal, discounts, extras } = saleReceiptLines(sale);
  for (const it of items) {
    out.push(
      `<div>${esc(it.cantidad)} x ${esc(it.nombre)}</div>` +
      rowHtml(`<span class="sm">${esc(it.sku || "")} · ${clp(it.unit_normal)} c/u</span>`,
              `<span class="b">${clp(it.amount_normal)}</span>`)
    );
  }

  if (sale.customer_name || sale.customer_rut) {
    out.push(`<div class="sep"></div>`);
    if (sale.customer_name) out.push(`<div class="sm">Cliente: ${esc(sale.customer_name)}</div>`);
    if (sale.customer_rut) out.push(`<div class="sm">RUT: ${esc(maskRut(sale.customer_rut))}</div>`);
  }

  out.push(`<div class="sep"></div>`);
  // Número de atención: con esto se llama al cliente cuando el pedido sale de cocina
  if (sale.numero_atencion) {
    out.push(`<div class="c b">N° DE ATENCIÓN</div><div class="c xxl">${esc(sale.numero_atencion)}</div>`);
    out.push(`<div class="c sm">Te llamaremos por este número</div><div class="sep"></div>`);
  }
  // Productos a precio normal arriba · los descuentos se detallan al final
  if (discounts.length) {
    out.push(rowHtml("Subtotal", clp(subtotal_normal)));
    for (const d of discounts) out.push(rowHtml(esc(d.label), `-${clp(d.value)}`, "promo"));
  }
  for (const ex of extras) out.push(rowHtml(esc(ex.label), clp(ex.value)));
  // Exenta (41): no se imprime neto ni IVA porque esos productos no pagan impuesto
  const totalExento = Math.round(sale.total_exento || 0);
  const totalAfecto = Math.round(sale.total_afecto ?? ((sale.total || 0) - totalExento));
  if (totalExento > 0) out.push(rowHtml("Exento", clp(totalExento)));
  if (totalAfecto > 0) {
    const iva = Math.round(sale.iva_afecto ?? sale.iva ?? 0);
    out.push(rowHtml("Neto", clp(Math.round(sale.neto_afecto ?? (totalAfecto - iva)))));
    out.push(rowHtml("IVA 19%", clp(iva)));
  }
  out.push(`<div class="sep"></div>`);
  out.push(rowHtml(`<span class="xxl">TOTAL</span>`, `<span class="xxl">${clp(sale.total)}</span>`));
  if (sale.redondeo_ajuste) {
    out.push(rowHtml("Redondeo legal", `${sale.redondeo_ajuste > 0 ? "+" : ""}${clp(sale.redondeo_ajuste)}`));
    out.push(rowHtml(`<span class="xl">A PAGAR</span>`, `<span class="xl">${clp(sale.total_pagar ?? sale.total)}</span>`));
  }
  // Pago: en mixto se detalla cada medio con su monto (sin la palabra "MIXTO")
  if (opts.paymentLines && opts.paymentLines.length) {
    out.push(`<div class="sep"></div>`);
    for (const pl of opts.paymentLines) out.push(rowHtml(esc(pl.label), clp(pl.value)));
  } else if ((sale.payments || []).length > 1) {
    out.push(`<div class="sep"></div>`);
    for (const p of sale.payments) {
      out.push(rowHtml(esc(p.nombre || String(p.codigo || "").replace(/_/g, " ")), clp(p.monto_cobrado ?? p.monto)));
    }
  } else {
    out.push(rowHtml("Pago", esc(opts.paymentLabel || sale.payment_label || String(sale.payment_method || "").replace(/_/g, " "))));
  }
  if (sale.credito) out.push(creditoHtml(sale.credito));
  if (sale.vuelto) out.push(rowHtml(`<span class="xl">VUELTO</span>`, `<span class="xl">${clp(sale.vuelto)}</span>`));
  // Fidelización: puntos ganados en esta venta y saldo acumulado (incluye esta compra)
  if (sale.loyalty_resumen) {
    const l = sale.loyalty_resumen;
    out.push(`<div class="sep"></div><div class="c b">PUNTOS DE FIDELIDAD</div>`);
    if (l.nombre) out.push(`<div class="c sm">${esc(l.nombre)}</div>`);
    out.push(rowHtml("Ganaste en esta compra", clp(l.gana || 0)));
    out.push(rowHtml(`<span class="b">Saldo acumulado</span>`, `<span class="b">${clp(l.acumulado || 0)}</span>`));
    if (l.tier) out.push(`<div class="c sm">Categoría ${esc(l.tier)}</div>`);
  }
  if (sale.quote_numero || sale.quote_id) {
    out.push(`<div class="c sm">Según cotización ${esc(String(sale.quote_numero || sale.quote_id))}</div>`);
  }
  if (sale.customer_name || sale.customer_rut) {
    out.push(`<div class="c sm">Cliente: ${esc([sale.customer_name, sale.customer_rut].filter(Boolean).join(" · "))}</div>`);
  }

  out.push(graciasHtml(opts.gracias_message));
  // Mensaje libre de la sucursal (horarios, redes sociales, etc.)
  for (const line of String(footerMessage || "").split("\n")) {
    if (line.trim()) out.push(`<div class="c sm">${esc(line.trim())}</div>`);
  }
  if (opts.qr) {
    out.push(`<div class="qr-wrap"><img class="qr" src="${esc(opts.qr)}" alt="">` +
             `<div class="sm">${esTicket ? "Escanea para ver tu ticket" : "Escanea para ver tu boleta digital"}</div></div>`);
  }
  out.push(tbkVoucherHtml(sale.tbk));
  return out.join("");
}

/** Boleta tradicional: datos del emisor, folio del SII y timbre electrónico PDF417 impreso.
 *  `receipt` es la respuesta de /public/receipt/{sale_id} (sale + company + store + dte). */
export function boletaLegalHtml(receipt, ctx = {}) {
  const { sale, company, store, dte } = receipt || {};
  if (!sale || !dte) return "";
  const e = dte.emisor || {}, r = dte.receptor || {}, t = dte.totales || {};
  const out = [];
  const fecha = (iso) => {
    const s = String(iso || "").slice(0, 10).split("-");
    return s.length === 3 ? `${s[2]}-${s[1]}-${s[0]}` : String(iso || "");
  };

  if (ctx.logo_url) out.push(`<img class="logo" src="${esc(ctx.logo_url)}" alt="">`);
  out.push(`<div class="c xl">${esc(e.razon_social || company?.nombre_comercial || "")}</div>`);
  out.push(`<div class="c sm">R.U.T. ${esc(e.rut || company?.rut || "")}</div>`);
  if (e.giro || company?.giro) out.push(`<div class="c sm">${esc(e.giro || company?.giro)}</div>`);
  // Casa matriz siempre; si emitió una sucursal, también la dirección de la sucursal
  const imp = dte.impresion_emisor || {};
  const cm = imp.casa_matriz || { direccion: e.direccion || company?.direccion, comuna: e.comuna || company?.comuna, ciudad: e.ciudad };
  const lugar = (d) => [d.direccion, d.comuna, d.ciudad && d.ciudad !== d.comuna ? d.ciudad : ""].filter(Boolean).map(esc).join(", ");
  if (cm.direccion) out.push(`<div class="c sm">${imp.sucursal ? "Casa matriz: " : ""}${lugar(cm)}</div>`);
  if (imp.sucursal) out.push(`<div class="c sm">Sucursal: ${lugar(imp.sucursal)}</div>`);
  else if (e.sucursal) out.push(`<div class="c sm">Sucursal SII: ${esc(e.sucursal)}</div>`);
  out.push(`<div class="sep"></div>`);
  out.push(`<div class="c b">${esc(String(dte.tipo_nombre || "DOCUMENTO ELECTRÓNICO").toUpperCase())}</div>`);
  out.push(`<div class="c xl">N° ${esc(dte.folio)}</div>`);
  // Oficina del SII (facturas): "S.I.I. - COYHAIQUE"
  if (["33", "34"].includes(String(dte.tipo_dte)) && dte.oficina_sii) out.push(`<div class="c b">S.I.I. - ${esc(dte.oficina_sii)}</div>`);
  const hora = sale.created_at ? new Date(sale.created_at).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "America/Santiago" }) : "";
  out.push(`<div class="c sm">Fecha: ${esc(fecha(dte.fecha_emision))}${hora ? ` · Hora: ${esc(hora)}` : ""}</div>`);
  if (ctx.caja) out.push(`<div class="c sm">Caja: ${esc(ctx.caja)}</div>`);
  if (ctx.cashier) out.push(`<div class="c sm">Cajero: ${esc(ctx.cashier)}</div>`);
  // Sección CLIENTE: en factura, nota de crédito y guía va completa y con el RUT a la vista
  const esCarta = ["33", "34", "46", "52", "56", "61"].includes(String(dte.tipo_dte));
  if (esCarta) {
    out.push(`<div class="sep"></div>`);
    out.push(`<div class="c b">CLIENTE</div>`);
    out.push(`<div class="sm">Razón social: ${esc(r.razon_social || sale.customer_name || "")}</div>`);
    out.push(`<div class="sm">R.U.T.: ${esc(r.rut || sale.customer_rut || "")}</div>`);
    if (r.giro || sale.customer_giro) out.push(`<div class="sm">Giro: ${esc(r.giro || sale.customer_giro)}</div>`);
    if (r.direccion || sale.customer_address) out.push(`<div class="sm">Dirección: ${esc(r.direccion || sale.customer_address)}</div>`);
    if (r.comuna || sale.customer_comuna) out.push(`<div class="sm">Comuna: ${esc(r.comuna || sale.customer_comuna)}</div>`);
    if (r.ciudad || sale.customer_ciudad) out.push(`<div class="sm">Ciudad: ${esc(r.ciudad || sale.customer_ciudad)}</div>`);
  } else if (r.rut && r.rut !== "66666666-6") {
    out.push(`<div class="sm">Cliente: ${esc(r.razon_social || "")}</div>`);
    out.push(`<div class="sm">R.U.T.: ${esc(maskRut(r.rut))}</div>`);
  }
  out.push(`<div class="sep"></div>`);

  // Productos a precio normal (los descuentos van detallados al final, como el vale con QR)
  const { items, subtotal_normal, discounts, extras } = saleReceiptLines(sale);
  for (const it of items) {
    out.push(
      `<div>${esc(it.cantidad)} x ${esc(it.nombre)}${it.es_exento ? " [E]" : ""}</div>` +
      rowHtml(`<span class="sm">${esc(it.sku || "")} · ${clp(it.unit_normal)} c/u</span>`,
              `<span class="b">${clp(it.amount_normal)}</span>`)
    );
  }
  out.push(`<div class="sep"></div>`);
  if (sale.numero_atencion) {
    out.push(`<div class="c b">N° DE ATENCIÓN</div><div class="c xxl">${esc(sale.numero_atencion)}</div><div class="sep"></div>`);
  }
  if (discounts.length) {
    out.push(rowHtml("Subtotal", clp(subtotal_normal)));
    for (const d of discounts) out.push(rowHtml(esc(d.label), `-${clp(d.value)}`, "promo"));
  }
  for (const dd of dte.descuentos || []) {
    out.push(rowHtml(esc(dd.glosa || "Descuento"), `${dd.tipo === "D" ? "-" : "+"}${clp(dd.valor)}`, "promo"));
  }
  for (const ex of extras) out.push(rowHtml(esc(ex.label), clp(ex.value)));
  if (t.exento > 0) out.push(rowHtml("Exento", clp(t.exento)));
  if (t.neto > 0) out.push(rowHtml("Neto", clp(t.neto)));
  if (t.iva > 0) out.push(rowHtml("IVA 19%", clp(t.iva)));
  out.push(rowHtml(`<span class="xxl">TOTAL</span>`, `<span class="xxl">${clp(t.total)}</span>`));
  if (sale.redondeo_ajuste) {
    out.push(rowHtml("Redondeo legal", `${sale.redondeo_ajuste > 0 ? "+" : ""}${clp(sale.redondeo_ajuste)}`));
    out.push(rowHtml(`<span class="xl">A PAGAR</span>`, `<span class="xl">${clp(sale.total_pagar ?? sale.total)}</span>`));
  }
  if (ctx.paymentLines && ctx.paymentLines.length) {
    out.push(`<div class="sep"></div>`);
    for (const pl of ctx.paymentLines) out.push(rowHtml(esc(pl.label), clp(pl.value)));
  } else {
    out.push(rowHtml("Pago", esc(ctx.paymentLabel || sale.payment_label || String(sale.payment_method || "").replace(/_/g, " "))));
  }
  if (sale.credito) out.push(creditoHtml(sale.credito));
  if (sale.vuelto) out.push(rowHtml(`<span class="xl">VUELTO</span>`, `<span class="xl">${clp(sale.vuelto)}</span>`));

  // Fidelización: puntos de esta compra y saldo acumulado
  if (sale.loyalty_resumen) {
    out.push(`<div class="sep"></div><div class="c b">PUNTOS DE FIDELIDAD</div>`);
    out.push(rowHtml("Ganaste en esta compra", clp(sale.loyalty_resumen.gana || 0)));
    out.push(rowHtml(`<span class="b">Saldo acumulado</span>`, `<span class="b">${clp(sale.loyalty_resumen.acumulado || 0)}</span>`));
  }

  // Timbre Electrónico SII (PDF417)
  if (dte.pdf417) {
    // Comentario del cliente: va impreso justo antes del timbre
    const obs = dte.observaciones || sale.doc_observaciones;
    if (obs) {
      out.push(`<div class="sep"></div><div class="b">OBSERVACIONES</div>`);
      out.push(`<div class="sm">${esc(obs)}</div>`);
    }
    const tr = dte.transporte;
    const refs = dte.referencias_doc || [];
    if (refs.length) {
      out.push(`<div class="sep"></div><div class="b">REFERENCIAS</div>`);
      for (const r of refs) out.push(`<div class="sm">${esc(r.nombre)} N° ${esc(r.folio)} · ${esc(String(r.fecha || "").split("-").reverse().join("-"))}${r.razon && r.razon !== r.nombre ? ` · ${esc(r.razon)}` : ""}</div>`);
    }
    if (tr && (tr.patente || tr.rut_chofer || tr.rut_transportista)) {
      out.push(`<div class="sep"></div><div class="b">TRANSPORTE</div>`);
      if (tr.nombre_chofer) out.push(`<div class="sm">Chofer: ${esc(tr.nombre_chofer)} · RUT ${esc(tr.rut_chofer || "-")}</div>`);
      if (tr.rut_transportista) out.push(`<div class="sm">RUT transportista: ${esc(tr.rut_transportista)}</div>`);
      if (tr.patente) out.push(`<div class="sm">Patente: ${esc(tr.patente)}</div>`);
    }
    // Copia cedible de la factura: acuse de recibo con espacio para que el cliente firme
    if (ctx.cedible) {
      out.push(`<div class="firma"><div class="c xl">ACUSE DE RECIBO</div>`);
      for (const l of ["NOMBRE", "RUT", "FECHA", "FIRMA"]) out.push(`<div class="ln"><span>${l}:</span><span></span></div>`);
      out.push(`</div>`);
    }
    // Espacio generoso para que el timbre no se pegue a la forma de pago
    out.push(`<div class="ted-wrap"></div>`);
    out.push(`<img class="ted" src="${esc(dte.pdf417)}" alt="">`);
    out.push(`<div class="c sm">Timbre Electrónico SII</div>`);
    if (dte.resolucion_num) {
      out.push(`<div class="c sm">Res. N° ${esc(dte.resolucion_num)} de ${esc(fecha(dte.resolucion_fecha))}</div>`);
    }
    out.push(`<div class="c sm">Verifique documento en www.sii.cl</div>`);
    if (dte.ambiente === "CERT") out.push(`<div class="c sm b">AMBIENTE DE CERTIFICACIÓN</div>`);
  }
  out.push(graciasHtml(ctx.gracias_message));
  if (sale?.quote_numero || sale?.quote_id) {
    out.push(`<div class="c sm">Según cotización ${esc(String(sale.quote_numero || sale.quote_id))}</div>`);
  }
  if (sale?.customer_name || sale?.customer_rut) {
    out.push(`<div class="c sm">Cliente: ${esc([sale.customer_name, sale.customer_rut].filter(Boolean).join(" · "))}</div>`);
  }
  for (const line of String(store?.ticket_footer || ctx.footer_message || "").split("\n")) {
    if (line.trim()) out.push(`<div class="c sm">${esc(line.trim())}</div>`);
  }
  if (ctx.cedible) out.push(`<div class="cedible">CEDIBLE</div>`);
  else out.push(tbkVoucherHtml(sale.tbk));
  return out.join("");
}

/** Segunda copia "CEDIBLE" de la factura: solo facturas y si el terminal la tiene activada. */
export function llevaCopiaCedible(legal, terminal) {
  return !!terminal?.factura_copia_cedible && ["33", "34"].includes(String(legal?.dte?.tipo_dte));
}

/** Voucher de ingreso/retiro de caja para la impresora de 80 mm. */
export function cashMovementHtml(mov, ctx = {}) {
  const out = [headHtml(ctx.company_name, ctx.store_name, `COMPROBANTE DE ${mov.tipo}`, ctx.logo_url)];
  if (ctx.caja) out.push(`<div class="sm">Caja: ${esc(ctx.caja)}</div>`);
  if (ctx.user_name) out.push(`<div class="sm">Cajero: ${esc(ctx.user_name)}</div>`);
  out.push(`<div class="sm">${esc(new Date(mov.at || Date.now()).toLocaleString("es-CL"))}</div>`);
  out.push(rowHtml("Tipo", esc(mov.tipo)));
  out.push(`<div class="sm">Motivo: ${esc(mov.motivo || "-")}</div>`);
  out.push(`<div class="sep"></div>`);
  out.push(rowHtml(`<span class="xxl">MONTO</span>`, `<span class="xxl">${clp(mov.monto)}</span>`));
  out.push(`<div class="sep"></div>`);
  out.push(`<div class="c sm">Documento interno · no válido como comprobante tributario</div>`);
  // En RETIRO: espacio al pie para nombre y firma de quien recibe el dinero.
  if (String(mov.tipo || "").toUpperCase().includes("RETIRO")) {
    out.push(`<div class="sep"></div>`);
    out.push(`<div class="sign"></div><div class="sm">Nombre: ____________________</div>`);
    out.push(`<div class="sign"></div><div class="sm">Firma: _____________________</div>`);
  } else {
    out.push(`<div class="sep"></div><div class="sign"></div><div class="sm">Firma: ____________________</div>`);
  }
  return out.join("");
}

/** Comprobante de cierre de caja (con detalle de arqueo si se contó billete por billete). */
/** Cuadre con el equipo Transbank: totales del POS vs ventas Transbank del turno. */
export function tbkCierreHtml(cuad) {
  const t = cuad?.tbk_cierre;
  if (!t) return "";
  const out = [`<div class="sep"></div><div class="b">TRANSBANK POS INTEGRADO</div>`];
  out.push(rowHtml("Ai-POS", `${t.sistema_cantidad} tx · ${clp(t.sistema_total)}`));
  if (t.totales_ok) {
    out.push(rowHtml("Equipo", `${t.equipo_cantidad} tx · ${clp(t.equipo_total)}`));
    out.push(rowHtml(`<span class="b">Diferencia</span>`, `<span class="b">${t.dif_cantidad} tx · ${clp(t.dif_total)}</span>`));
    out.push(`<div class="c sm b">${t.cuadra ? "CUADRA CON EL EQUIPO" : "NO CUADRA CON EL EQUIPO"}</div>`);
  } else out.push(`<div class="sm">Totales del equipo no disponibles</div>`);
  out.push(`<div class="c sm b">${t.cierre_ok ? "CIERRE TRANSBANK REALIZADO" : "CIERRE TRANSBANK NO REALIZADO"}</div>`);
  if (!t.cierre_ok && t.mensaje) out.push(`<div class="c sm">${esc(t.mensaje)}</div>`);
  return out.join("");
}

export function cashCloseHtml(cuad, ctx = {}) {
  const out = [headHtml(ctx.company_name, ctx.store_name, "CIERRE DE CAJA", ctx.logo_url)];
  if (ctx.user_name) out.push(`<div class="sm">Cajero: ${esc(ctx.user_name)}</div>`);
  out.push(`<div class="sm">Caja: ${esc(cuad.caja || "-")}</div>`);
  if (cuad.opened_at) out.push(`<div class="sm">Apertura: ${esc(new Date(cuad.opened_at).toLocaleString("es-CL"))}</div>`);
  out.push(`<div class="sep"></div><div class="b">VENTAS POR MEDIO DE PAGO</div>`);
  for (const m of cuad.medios || []) {
    out.push(rowHtml(`${esc(m.nombre || String(m.codigo).replace(/_/g, " "))} <span class="sm">· ${m.n}</span>`, clp(m.monto)));
  }
  if (!(cuad.medios || []).length) out.push(`<div class="sm">Sin ventas en el turno</div>`);
  out.push(`<div class="sep"></div>`);
  out.push(rowHtml("Ventas cobradas", clp(cuad.ventas_cobrado)));
  if (cuad.redondeo_dia) out.push(rowHtml("Redondeo del día", clp(cuad.redondeo_dia)));
  if ((cuad.abonos_credito || []).length) {
    out.push(`<div class="sep"></div><div class="b">PAGOS DE CRÉDITO</div>`);
    for (const m of cuad.abonos_credito) out.push(rowHtml(`${esc(m.nombre)} <span class="sm">· ${m.n}</span>`, clp(m.monto)));
    out.push(rowHtml("<b>Total pagos de crédito</b>", `<b>${clp(cuad.abonos_total)}</b>`));
    out.push(`<div class="sep"></div><div class="b">TOTAL GENERAL (VENTAS + PAGOS)</div>`);
    for (const m of cuad.total_general || []) out.push(rowHtml(esc(m.nombre), clp(m.monto)));
    out.push(rowHtml("<b>Total recibido</b>", `<b>${clp(cuad.total_general_monto)}</b>`));
    out.push(`<div class="sep"></div>`);
  }
  out.push(rowHtml("Fondo inicial", clp(cuad.fondo_inicial)));
  if (cuad.ingresos) out.push(rowHtml("Ingresos", `+${clp(cuad.ingresos)}`));
  if (cuad.retiros) out.push(rowHtml("Retiros", `-${clp(cuad.retiros)}`));
  out.push(`<div class="sep"></div>`);
  out.push(rowHtml(`<span class="xl">ESPERADO</span>`, `<span class="xl">${clp(cuad.efectivo_esperado)}</span>`));
  if (ctx.arqueo_detalle?.length) {
    out.push(`<div class="sep"></div><div class="b">ARQUEO · DETALLE CONTADO</div>`);
    for (const d of ctx.arqueo_detalle) {
      out.push(rowHtml(`${clp(d.denominacion)} × ${d.cantidad}`, clp(d.subtotal)));
    }
  }
  if (cuad.efectivo_contado != null) {
    out.push(`<div class="sep"></div>`);
    out.push(rowHtml(`<span class="xl">CONTADO</span>`, `<span class="xl">${clp(cuad.efectivo_contado)}</span>`));
    out.push(rowHtml(`<span class="xxl">DIFERENCIA</span>`, `<span class="xxl">${clp(cuad.diferencia)}</span>`));
  }
  if (cuad.ventas_boleta?.cantidad || cuad.ventas_factura?.cantidad) {
    out.push(`<div class="sep"></div><div class="b">VENTAS POR DOCUMENTO</div>`);
    if (cuad.ventas_boleta?.cantidad) out.push(rowHtml(`Boletas (${esc(cuad.ventas_boleta.cantidad)})`, clp(cuad.ventas_boleta.total)));
    if (cuad.ventas_factura?.cantidad) out.push(rowHtml(`Facturas (${esc(cuad.ventas_factura.cantidad)})`, clp(cuad.ventas_factura.total)));
  }
  const tks = cuad.tickets_sin_dte;
  if (tks?.cantidad) {
    out.push(`<div class="sep"></div><div class="b">TICKETS SIN DTE</div>`);
    out.push(rowHtml("Tickets emitidos", esc(tks.cantidad)));
    out.push(rowHtml(`<span class="b">Vendido sin DTE</span>`, `<span class="b">${clp(tks.total)}</span>`));
    out.push(`<div class="sm">No van al libro de ventas. Incluidos en los totales de arriba.</div>`);
  }
  out.push(tbkCierreHtml(cuad));
  if (cuad.c2c_sistema?.cantidad) out.push(`<div class="sep"></div><div class="b">TRANSBANK C2C</div>${rowHtml("Ai-POS", `${cuad.c2c_sistema.cantidad} tx · ${clp(cuad.c2c_sistema.total)}`)}`);
  out.push(`<div class="sep"></div><div class="c sm">Ventas: ${cuad.num_ventas ?? 0}</div>`);
  if (cuad.ventas_nulas) out.push(`<div class="c sm b">Ventas nulas: ${cuad.ventas_nulas} · ${clp(cuad.monto_nulas)}</div>`);
  if (cuad.ventas_simuladas) out.push(`<div class="c sm">Incluye ${clp(cuad.ventas_simuladas)} simulados</div>`);
  out.push(`<div class="sep"></div><div class="sign"></div><div class="sm">Firma cajero: ______________</div>`);
  return out.join("");
}

/** Vale de venta suspendida: se imprime igual que el vale de venta. */
export function suspendedTicketHtml(susp, ctx = {}) {
  const out = [headHtml(ctx.company_name, ctx.store_name, "VENTA SUSPENDIDA", ctx.logo_url)];
  if (ctx.caja) out.push(`<div class="sm">Caja: ${esc(ctx.caja)}</div>`);
  if (ctx.cashier) out.push(`<div class="sm">Cajero: ${esc(ctx.cashier)}</div>`);
  for (const it of susp.items || []) {
    out.push(`<div>${esc(it.cantidad)} x ${esc(it.nombre)}</div>`);
    out.push(rowHtml(`<span class="sm">${esc(it.sku || "")}</span>`,
      `<span class="b">${clp((it.precio_unitario || 0) * (it.cantidad || 0))}</span>`));
  }
  out.push(`<div class="sep"></div>`);
  out.push(rowHtml(`<span class="xxl">TOTAL</span>`, `<span class="xxl">${clp(susp.total)}</span>`));
  out.push(`<div class="sep"></div>`);
  if (ctx.barcode) out.push(`<img class="bc" src="${esc(ctx.barcode)}" alt="">`);
  else out.push(`<div class="c xxl">${esc(susp.codigo || "")}</div>`);
  out.push(`<div class="c sm">Presenta este código en caja para pagar</div>`);
  out.push(`<div class="c sm b">NO es comprobante de pago</div>`);
  for (const line of String(ctx.footer_message || "").split("\n")) {
    if (line.trim()) out.push(`<div class="c sm">${esc(line.trim())}</div>`);
  }
  return out.join("");
}

/** Vale de pre-venta: lleva el código de barras para que la caja lo escanee. */
export function presaleTicketHtml(presale, ctx = {}) {
  const out = [headHtml(ctx.company_name, ctx.store_name, "PRE-VENTA", ctx.logo_url)];
  if (ctx.caja) out.push(`<div class="sm">Caja: ${esc(ctx.caja)}</div>`);
  if (ctx.cashier) out.push(`<div class="sm">Vendedor: ${esc(ctx.cashier)}</div>`);
  for (const it of presale.items || []) {
    out.push(`<div>${esc(it.cantidad)} x ${esc(it.nombre)}</div>`);
    out.push(rowHtml(`<span class="sm">${esc(it.sku || "")}</span>`,
      `<span class="b">${clp(it.subtotal ?? ((it.precio_unitario || 0) * (it.cantidad || 0)))}</span>`));
  }
  if (presale.cliente_nombre) out.push(`<div class="sep"></div><div class="sm">Cliente: ${esc(presale.cliente_nombre)}</div>`);
  if (presale.cliente_rut) out.push(`<div class="sm">RUT: ${esc(presale.cliente_rut)}</div>`);
  out.push(`<div class="sep"></div>`);
  out.push(rowHtml(`<span class="xxl">TOTAL</span>`, `<span class="xxl">${clp(presale.total)}</span>`));
  out.push(`<div class="sep"></div>`);
  if (ctx.barcode) out.push(`<img class="bc" src="${esc(ctx.barcode)}" alt="">`);
  out.push(`<div class="c xl">${esc(presale.codigo || "")}</div>`);
  out.push(`<div class="c sm">Paga este vale en caja</div>`);
  out.push(`<div class="c sm b">NO es comprobante de pago</div>`);
  for (const line of String(ctx.footer_message || "").split("\n")) {
    if (line.trim()) out.push(`<div class="c sm">${esc(line.trim())}</div>`);
  }
  return out.join("");
}

/** Vale de concurso para la impresora de 80 mm. Se imprime como trabajo separado
 *  (tras cortar la boleta) y replica el formato del backoffice:
 *  CONCURSO / nombre / detalle / QR / código / Sucursal / Venta Nº / Monto /
 *  datos cliente / DEPOSITA TU VALE EN LA TÓMBOLA. */
export function contestVoucherHtml(v, ctx = {}) {
  const out = [];
  out.push(`<div class="c sm b">CONCURSO</div>`);
  out.push(`<div class="c xl">${esc((v.contest_name || "").toUpperCase())}</div>`);
  if (v.contest_detail) out.push(`<div class="c sm">${esc(v.contest_detail)}</div>`);
  out.push(`<div class="sep"></div>`);
  if (ctx.qr) out.push(`<img class="qr" src="${esc(ctx.qr)}" alt="">`);
  out.push(`<div class="c b">${esc(v.voucher_code || "")}</div>`);
  out.push(`<div class="sep"></div>`);
  out.push(rowHtml("Sucursal", esc(v.store_identifier || v.store_id || "")));
  out.push(rowHtml("Venta N", esc(String(v.sale_number || "-"))));
  out.push(`<div class="sm">${esc(new Date(v.created_at || Date.now()).toLocaleString("es-CL", { hour12: false }))}</div>`);
  out.push(rowHtml("Monto", clp(v.sale_total || 0)));
  if (ctx.caja) out.push(`<div class="sm">Caja: ${esc(ctx.caja)}</div>`);
  if (ctx.cashier) out.push(`<div class="sm">Cajero: ${esc(ctx.cashier)}</div>`);
  out.push(`<div class="sep"></div>`);
  if (v.is_loyalty) {
    out.push(`<div class="sm">Nombre: ${esc(v.customer_name || v.customer_email || "")}</div>`);
    out.push(`<div class="sm">Contacto: ${esc(v.customer_phone || v.customer_email || "-")}</div>`);
  } else {
    // Espacio amplio para que el cliente escriba a mano sus datos.
    out.push(`<div class="c sm b">Completa tus datos</div>`);
    out.push(`<div class="sm" style="margin-top:3mm;text-align:left">Nombre:</div>`);
    out.push(`<div class="wline"></div>`);
    out.push(`<div class="sm" style="margin-top:3mm;text-align:left">Contacto / Teléfono:</div>`);
    out.push(`<div class="wline"></div>`);
  }
  out.push(`<div class="sep"></div>`);
  out.push(`<div class="c sm b">DEPOSITA TU VALE EN LA TOMBOLA</div>`);
  return out.join("");
}

/** Ticket de cambio (regalo): identifica la venta para gestionar el cambio en administración.
 *  No lleva precios: `ctx.items` son las líneas a mostrar y `ctx.barcode` el código impreso. */
export function exchangeSlipHtml(sale, ctx = {}) {
  const num = String(sale?.numero ?? "");
  const out = [headHtml(ctx.company_name, ctx.store_name, "TICKET DE CAMBIO", ctx.logo_url)];
  out.push(`<div class="sm">Venta N° ${esc(num)}</div>`);
  if (ctx.caja) out.push(`<div class="sm">Caja: ${esc(ctx.caja)}</div>`);
  out.push(`<div class="sep"></div>`);
  for (const it of ctx.items || []) {
    out.push(`<div>${esc(it.cantidad)} x ${esc(it.nombre)}</div>`);
    if (it.sku) out.push(`<div class="sm">${esc(it.sku)}</div>`);
  }
  out.push(`<div class="sep"></div>`);
  if (ctx.barcode) out.push(`<img class="bc" src="${esc(ctx.barcode)}" alt="">`);
  else out.push(`<div class="c xxl">${esc(num)}</div>`);
  out.push(`<div class="c sm">Presenta este ticket en administración para gestionar tu cambio</div>`);
  out.push(`<div class="c sm b">NO es comprobante de pago · sin valor comercial</div>`);
  return out.join("");
}

/** .bat que abre el POS en Chrome/Edge a pantalla completa con impresión directa.
 *  Clave: `--user-data-dir` propio, si no Chrome reutiliza el proceso abierto e ignora el flag.
 *  `--kiosk` deja la caja a pantalla completa (se sale con Alt+F4). */
const asciiOnly = (s) => String(s ?? "")
  .normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^\x20-\x7E]/g, "");

/** PowerShell → base64 UTF-16LE para `-EncodedCommand` (evita todo el escapado de cmd.exe). */
function psEncoded(script) {
  let bin = "";
  for (let i = 0; i < script.length; i++) {
    const c = script.charCodeAt(i);
    bin += String.fromCharCode(c & 0xff, (c >> 8) & 0xff);
  }
  return btoa(bin);
}

/** Ventana "Cargando Ai-POS" de Windows (estilo splash de aplicación de escritorio).
 *  Se muestra mientras Chrome arranca y se cierra cuando la ventana del POS ya existe. */
function splashPowerShell(origin, terminalName, version = "") {
  return [
    "$ErrorActionPreference = 'SilentlyContinue'",
    "Add-Type -AssemblyName System.Windows.Forms",
    "Add-Type -AssemblyName System.Drawing",
    "[System.Windows.Forms.Application]::EnableVisualStyles()",
    `$origen = '${origin}'`,
    `$caja = '${terminalName}'`,
    `$ver = '${asciiOnly(version)}'`,
    "$logo = Join-Path $env:TEMP 'aipos-splash-logo.png'",
    "if (-not (Test-Path $logo)) { try { (New-Object Net.WebClient).DownloadFile(\"$origen/brand/aipos-logo.png\", $logo) } catch { } }",
    "$f = New-Object Windows.Forms.Form",
    "$f.FormBorderStyle = 'None'",
    "$f.StartPosition = 'CenterScreen'",
    "$f.ClientSize = New-Object Drawing.Size(580,330)",
    "$f.BackColor = [Drawing.ColorTranslator]::FromHtml('#0D1430')",
    "$f.TopMost = $true",
    "$f.ShowInTaskbar = $false",
    "$borde = New-Object Windows.Forms.Panel",
    "$borde.Dock = 'Top'; $borde.Height = 5",
    "$borde.BackColor = [Drawing.ColorTranslator]::FromHtml('#0EA5E9')",
    "$f.Controls.Add($borde)",
    "if (Test-Path $logo) {",
    "  $pic = New-Object Windows.Forms.PictureBox",
    "  $pic.SizeMode = 'Zoom'",
    "  $pic.BackColor = [Drawing.ColorTranslator]::FromHtml('#0D1430')",
    "  $pic.Location = New-Object Drawing.Point(40,58)",
    "  $pic.Size = New-Object Drawing.Size(88,88)",
    "  $pic.Image = [Drawing.Image]::FromFile($logo)",
    "  $f.Controls.Add($pic)",
    "}",
    "$lbl = New-Object Windows.Forms.Label",
    "$lbl.Text = 'Ai-POS'",
    "$lbl.Font = New-Object Drawing.Font('Segoe UI',28,[Drawing.FontStyle]::Bold)",
    "$lbl.ForeColor = [Drawing.Color]::White",
    "$lbl.AutoSize = $true",
    "$lbl.Location = New-Object Drawing.Point(146,52)",
    "$f.Controls.Add($lbl)",
    "$sub = New-Object Windows.Forms.Label",
    "$sub.Text = 'Punto de venta con inteligencia artificial'",
    "$sub.Font = New-Object Drawing.Font('Segoe UI',10)",
    "$sub.ForeColor = [Drawing.ColorTranslator]::FromHtml('#93C5FD')",
    "$sub.AutoSize = $true",
    "$sub.Location = New-Object Drawing.Point(150,112)",
    "$f.Controls.Add($sub)",
    "$cj = New-Object Windows.Forms.Label",
    "$cj.Text = 'Terminal: ' + $caja",
    "$cj.Font = New-Object Drawing.Font('Segoe UI',9)",
    "$cj.ForeColor = [Drawing.ColorTranslator]::FromHtml('#94A3B8')",
    "$cj.AutoSize = $true",
    "$cj.Location = New-Object Drawing.Point(150,142)",
    "$f.Controls.Add($cj)",
    "$vr = New-Object Windows.Forms.Label",
    "if ($ver -ne '') { $vr.Text = 'Version ' + $ver } else { $vr.Text = '' }",
    "$vr.Font = New-Object Drawing.Font('Segoe UI',9)",
    "$vr.ForeColor = [Drawing.ColorTranslator]::FromHtml('#64748B')",
    "$vr.AutoSize = $true",
    "$vr.Location = New-Object Drawing.Point(150,164)",
    "$f.Controls.Add($vr)",
    "$pb = New-Object Windows.Forms.ProgressBar",
    "$pb.Style = 'Marquee'",
    "$pb.MarqueeAnimationSpeed = 28",
    "$pb.Location = New-Object Drawing.Point(40,236)",
    "$pb.Size = New-Object Drawing.Size(500,8)",
    "$f.Controls.Add($pb)",
    "$st = New-Object Windows.Forms.Label",
    "$st.Text = 'CARGANDO AI-POS...'",
    "$st.Font = New-Object Drawing.Font('Segoe UI',9,[Drawing.FontStyle]::Bold)",
    "$st.ForeColor = [Drawing.ColorTranslator]::FromHtml('#CBD5E1')",
    "$st.AutoSize = $true",
    "$st.Location = New-Object Drawing.Point(40,256)",
    "$f.Controls.Add($st)",
    "$lg = New-Object Windows.Forms.Label",
    "$lg.Text = [char]0x00A9 + ' Ai-POS - Todos los derechos reservados'",
    "$lg.Font = New-Object Drawing.Font('Segoe UI',8)",
    "$lg.ForeColor = [Drawing.ColorTranslator]::FromHtml('#64748B')",
    "$lg.AutoSize = $true",
    "$lg.Location = New-Object Drawing.Point(40,292)",
    "$f.Controls.Add($lg)",
    "$f.Show()",
    "$f.Activate()",
    "$t0 = Get-Date",
    "while ($true) {",
    "  [System.Windows.Forms.Application]::DoEvents()",
    "  Start-Sleep -Milliseconds 120",
    "  $seg = ((Get-Date) - $t0).TotalSeconds",
    "  if ($seg -gt 3) {",
    "    $w = Get-Process chrome,msedge -ErrorAction SilentlyContinue | Where-Object { $_.MainWindowHandle -ne 0 -and $_.MainWindowTitle -ne '' }",
    "    if ($w) { Start-Sleep -Milliseconds 900; break }",
    "  }",
    "  if ($seg -gt 25) { break }",
    "}",
    "$f.Close()",
  ].join("\n");
}

/** Crea (una sola vez) un acceso directo en el escritorio con el icono de Ai-POS.
 *  El .bat queda como destino, pero el usuario ve el icono del programa, no el de un .bat.
 *  La carpeta Escritorio se resuelve con .NET (así funciona también con OneDrive). */
export function desktopIconBatLines(origin, terminalNameRaw) {
  const terminalName = asciiOnly(terminalNameRaw) || "Caja";
  const safe = terminalName.replace(/[^A-Za-z0-9 ]/g, "").trim() || "Caja";
  const ps = [
    "$d=[Environment]::GetFolderPath('Desktop')",
    "if (-not $d -or -not (Test-Path $d)) { $d=Join-Path $env:USERPROFILE 'Desktop' }",
    `$p=Join-Path $d 'Ai-POS ${safe}.lnk'`,
    "if (-not (Test-Path $p)) { $s=(New-Object -ComObject WScript.Shell).CreateShortcut($p); $s.TargetPath='%~f0'; $s.WorkingDirectory='%~dp0'; $s.IconLocation='%AIPOSICO%'; $s.Description='Ai-POS - punto de venta'; $s.WindowStyle=7; $s.Save() }",
  ].join("; ");
  return [
    "REM ---- Icono de Ai-POS en el escritorio (se crea la primera vez) ----",
    'if not exist "%LocalAppData%\\Ai-POS" mkdir "%LocalAppData%\\Ai-POS"',
    'set "AIPOSICO=%LocalAppData%\\Ai-POS\\aipos.ico"',
    `if not exist "%AIPOSICO%" powershell -NoProfile -ExecutionPolicy Bypass -Command "(New-Object Net.WebClient).DownloadFile('${origin}/brand/aipos.ico','%AIPOSICO%')"`,
    `powershell -NoProfile -ExecutionPolicy Bypass -Command "${ps}"`,
  ];
}

/** Líneas de .bat que muestran la ventana "Cargando Ai-POS" mientras arranca Chrome.
 *  El base64 va troceado a un archivo: cmd.exe no acepta líneas de más de ~8.191 caracteres. */
export function splashBatLines(origin, terminalNameRaw, version = "") {
  const terminalName = asciiOnly(terminalNameRaw) || "Caja";
  const trozos = psEncoded(splashPowerShell(origin, terminalName, version)).match(/.{1,440}/g) || [];
  return [
    "REM ---- Pantalla de inicio 'Cargando Ai-POS' (se cierra al abrir la ventana de la caja) ----",
    'set "AIPOSB64=%TEMP%\\aipos-splash.b64"',
    `>"%AIPOSB64%" echo ${trozos[0] || ""}`,
    ...trozos.slice(1).map((t) => `>>"%AIPOSB64%" echo ${t}`),
    'start "" /b powershell -NoProfile -ExecutionPolicy Bypass -WindowStyle Hidden -Command "iex ([Text.Encoding]::Unicode.GetString([Convert]::FromBase64String((Get-Content -Raw \'%AIPOSB64%\'))))"',
  ];
}

// modo KIOSCO: pantalla completa sin barra · NORMAL: ventana maximizada con la barra del navegador
export function buildDirectPrintBat(url, printerNameRaw, terminalNameRaw, modo = "KIOSCO") {
  const terminalName = asciiOnly(terminalNameRaw) || "Caja";
  const printerName = asciiOnly(printerNameRaw);
  let origin = "";
  try { origin = new URL(url).origin; } catch (_) { origin = ""; }
  const splash = psEncoded(splashPowerShell(origin, terminalName, APP_VERSION));
  // cmd.exe no acepta líneas de más de ~8.191 caracteres: el base64 va troceado a un archivo
  const trozos = splash.match(/.{1,440}/g) || [];
  const normal = modo === "NORMAL";
  const flags = (normal ? "--start-maximized " : "--kiosk --start-fullscreen ") +
    "--kiosk-printing --no-first-run --no-default-browser-check " +    "--enable-features=WebBluetoothNewPermissionsBackend --enable-experimental-web-platform-features";
  return [
    "@echo off",
    `title Ai-POS ${terminalName} - impresion directa`,
    normal ? "REM Ai-POS - Caja en ventana normal (con barra del navegador) e impresion directa"
      : "REM Ai-POS - Caja a pantalla completa con impresion directa (sin dialogo de Windows)",
    printerName ? `REM Impresora configurada en Ai-POS: ${printerName}` : "",
    "REM REQUISITO: esa impresora debe estar como PREDETERMINADA en Windows.",
    "REM El perfil propio (--user-data-dir) es obligatorio: sin el, Chrome reutiliza la ventana",
    "REM ya abierta e ignora el modo de impresion directa.",
    `set "AIPOSURL=${url}"`,
    `set "AIPOSPROFILE=%LocalAppData%\\Ai-POS-${terminalName.replace(/[^A-Za-z0-9]/g, "")}"`,
    "",
    ...desktopIconBatLines(origin, terminalName),
    "",
    "REM ---- Pantalla de inicio 'Cargando Ai-POS' (se cierra al abrir la ventana de la caja) ----",
    'set "AIPOSB64=%TEMP%\\aipos-splash.b64"',
    `>"%AIPOSB64%" echo ${trozos[0] || ""}`,
    ...trozos.slice(1).map((t) => `>>"%AIPOSB64%" echo ${t}`),
    'start "" /b powershell -NoProfile -ExecutionPolicy Bypass -WindowStyle Hidden -Command "iex ([Text.Encoding]::Unicode.GetString([Convert]::FromBase64String((Get-Content -Raw \'%AIPOSB64%\'))))"',
    "",
    'set "CHROME=%ProgramFiles%\\Google\\Chrome\\Application\\chrome.exe"',
    'if not exist "%CHROME%" set "CHROME=%ProgramFiles(x86)%\\Google\\Chrome\\Application\\chrome.exe"',
    'if not exist "%CHROME%" set "CHROME=%LocalAppData%\\Google\\Chrome\\Application\\chrome.exe"',
    'set "EDGE=%ProgramFiles(x86)%\\Microsoft\\Edge\\Application\\msedge.exe"',
    'if not exist "%EDGE%" set "EDGE=%ProgramFiles%\\Microsoft\\Edge\\Application\\msedge.exe"',
    'if exist "%CHROME%" (',
    `  start "" "%CHROME%" ${flags} --user-data-dir="%AIPOSPROFILE%" "%AIPOSURL%"`,
    ') else if exist "%EDGE%" (',
    `  start "" "%EDGE%" ${flags} --user-data-dir="%AIPOSPROFILE%-edge" "%AIPOSURL%"`,
    ') else (',
    "  echo No se encontro Chrome ni Edge en este equipo.",
    "  pause",
    ")",
  ].filter(Boolean).join("\r\n");
}

/** Nombre de archivo ASCII seguro: sin espacios, acentos ni paréntesis (cmd.exe los rompe). */
function batFileName(terminalName, modo) {
  const safe = String(terminalName || "Caja")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "Caja";
  return `Ai-POS-${safe}-${modo === "NORMAL" ? "ventana-normal" : "pantalla-completa"}.bat`;
}

export function downloadDirectPrintShortcut(url, printerName, terminalName, modo = "KIOSCO") {
  // Sin BOM: un .bat que empieza con BOM hace fallar cmd.exe ("@echo no se reconoce").
  const bat = buildDirectPrintBat(url, printerName, terminalName, modo);
  const blob = new Blob([bat], { type: "text/plain;charset=us-ascii" });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = batFileName(terminalName, modo);
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(href); a.remove(); }, 2000);
  return { filename: a.download, content: bat };
}

/** Comprobante de apertura de caja con el fondo inicial declarado. */
export function cashOpenHtml(data, ctx = {}) {
  const out = [headHtml(ctx.company_name, ctx.store_name, "APERTURA DE CAJA", ctx.logo_url)];
  if (ctx.caja) out.push(`<div class="sm">Caja: ${esc(ctx.caja)}</div>`);
  if (ctx.user_name) out.push(`<div class="sm">Cajero: ${esc(ctx.user_name)}</div>`);
  out.push(`<div class="sm">Fecha: ${esc(new Date(data.opened_at || Date.now()).toLocaleDateString("es-CL"))}</div>`);
  out.push(`<div class="sm">Hora: ${esc(new Date(data.opened_at || Date.now()).toLocaleTimeString("es-CL"))}</div>`);
  out.push(`<div class="sep"></div>`);
  out.push(rowHtml(`<span class="xxl">FONDO INICIAL</span>`, `<span class="xxl">${clp(data.fondo_inicial || 0)}</span>`));
  out.push(`<div class="sep"></div>`);
  out.push(`<div class="c sm">Documento interno · no válido como comprobante tributario</div>`);
  out.push(`<div class="sep"></div><div class="sign"></div><div class="sm">Firma cajero: ______________</div>`);
  return out.join("");
}
